import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import {
  CompanyNotificationDateInfoResponse,
  getBenefitEligibilityDate,
  getCompanyUserNotificationDateInfo,
} from 'core'
import { EmploymentStatus } from 'core/application/adminDashboard/managePeople/enums/employmentStatus'
import addPeople from 'core/application/companies/addPeople'
import SaveCompanyUserRequest from 'core/application/companies/common/saveCompanyUserRequest'
import {
  EmployeeForSaveDto,
  ProductClassForEmployeeForSave,
  filterProductClassesByEmploymentStatus,
  getEmployeeForSave,
} from 'core/application/companies/getEmployeeForSave'
import isEmployeeEligible from 'core/application/companies/isEmployeeEligible'

import { zipNumberRegex } from 'common/components/utils/InputUtils'
import { filterByKeyAndValue } from 'common/components/utils/SelectFilterUtils'

import {
  Alert,
  Button,
  Category,
  Col,
  DatePicker,
  DefaultDatePickerV2,
  DefaultInput,
  DefaultSelect,
  Icon,
  InputMask,
  Label,
  Option,
  Radio,
  RedAsterisk,
  Row,
  SectionBlock,
  Skeleton,
} from 'common/components'
import { disabledBirthDate, isAfter, isBefore, nowAsString, parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import './styles.less'

interface AddPeopleFormProps {
  onEmployeeSaved?: (addMore: boolean) => void
  onCancel?: () => void
}

const AddPeopleForm: React.FC<AddPeopleFormProps> = (props) => {
  const { t: translate } = useTranslation()

  const {
    data: employee,
    isLoading: isEmployeeLoading,
    reload: reloadEmployee,
  } = useQueryRequestV2<EmployeeForSaveDto>({ queryFn: () => getEmployeeForSave() })
  const { data: eligibilityDate, sendQuery: sendGetEligibilityDate } = useQueryRequestV2({ initialState: null })
  const { sendCommand: sendAddPeopleCommand, isLoading: isCommandLoading } = useCommandRequestV2(
    (request: SaveCompanyUserRequest) => addPeople(request),
  )
  const [initialValues, setInitialValues] = useState<FormData | null>()
  const [productClasses, setProductClasses] = useState<ProductClassForEmployeeForSave[]>([])
  const [renewalProductClasses, setRenewalProductClasses] = useState<ProductClassForEmployeeForSave[]>([])
  const { data: userNotificationDateInfo, sendQuery: sendGetCompanyUserNotificationDateInfo } =
    useQueryRequestV2<CompanyNotificationDateInfoResponse>({
      initialState: {
        isEmployeeNotificationDateEditable: false,
        notificationDate: null,
        maxNotificationDate: null,
        minNotificationDate: nowAsString(),
        requireEmployeeNotificationDate: false,
      },
    })

  useEffect(() => {
    ;(async () => {
      if (employee) {
        setInitialValues({
          ...employee,
          hireDateSelect: null,
          hireDate: null,
        })
        setProductClasses(employee.productClasses)
        setRenewalProductClasses(employee.renewalProductClasses)

        await sendGetCompanyUserNotificationDateInfo(() =>
          getCompanyUserNotificationDateInfo({
            companyUserId: employee.companyUserId,
            planPeriodId: employee.planPeriodId,
          }),
        )
      }
    })()
  }, [employee])

  const submitForm = async (formik: FormikProps<any>, addMore: boolean) => {
    const request: SaveCompanyUserRequest = {
      ...formik.values,
      hireDate: formik.values.hireDate ?? employee.benefitStartDate,
      zipCode: formik.values.zipCode ? formik.values.zipCode.toString() : null,
    }

    const result = await sendAddPeopleCommand(request)
    if (result.completed) {
      formik.resetForm()
      reloadEmployee()
      if (props.onEmployeeSaved) {
        props.onEmployeeSaved(addMore)
      }
    }
  }

  const handleEligibilityDateChange = async (
    productClassId: string,
    hireDateSelect: number,
    hireDate: string,
    benefitStartDate: string,
    formik: FormikProps<FormData>,
  ) => {
    if (productClassId) {
      const result = await sendGetEligibilityDate(() =>
        getBenefitEligibilityDate(productClassId, hireDateSelect === 0 ? hireDate : benefitStartDate),
      )

      /* This is an edge case.
       * The user initially selects a hire date which results in the notification date to be disabled.
       * Then the user changes the hire date to a date that makes the notification date editable.
       * After that the user changes again the hire date to a date that makes the notification date disabled.
       * In this case we need to define the notification date to today's date.
       * Example of that can be found on the comments section on https://zanebenefits.atlassian.net/browse/IC-274
       * */
      const isEditableBeforeTheNewNotificationInfo = userNotificationDateInfo.isEmployeeNotificationDateEditable

      const notificationDateInfoResponse = await sendGetCompanyUserNotificationDateInfo(() =>
        getCompanyUserNotificationDateInfo({
          companyUserId: employee.companyUserId,
          planPeriodId: employee.planPeriodId,
          overrideBenefitEligibilityDate: result,
        }),
      )

      if (!notificationDateInfoResponse.isEmployeeNotificationDateEditable && isEditableBeforeTheNewNotificationInfo) {
        await formik.setFieldValue('notificationDate', nowAsString())
      }
    }
  }

  const showWaitingPeriodAlert = (formik: FormikProps<FormData>) =>
    (formik.values.hireDateSelect === 1 || (formik.values.hireDateSelect === 0 && formik.values.hireDate)) &&
    formik.values.employmentStatus !== null

  return (
    <Skeleton active={true} loading={isEmployeeLoading}>
      {initialValues && (
        <Formik
          enableReinitialize={true}
          validationSchema={formValidator(translate, initialValues, productClasses, renewalProductClasses)}
          initialValues={initialValues}
          onSubmit={() => {}}
        >
          {(formik) => (
            <form
              onSubmit={formik.handleSubmit}
              autoComplete="off"
              className={'perks-add-people-wrapper form-manage-people-wrapper'}
            >
              <p>
                Please enter all required fields marked with an asterisk <span className={'color-red-6'}>*</span>.
              </p>
              <SectionBlock
                className="mb-16"
                mobileOnly={true}
                titleElement={
                  <>
                    <Icon className="far fa-user mr-8" />
                    <Category title={translate('admin-dashboard.manage-people.complete-profile.header-1')} />
                  </>
                }
              >
                <Row>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} className="section-block_content-item mb-32">
                    <DefaultInput showRequiredSign={true} label="First Name" name="firstName" />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} className="section-block_content-item mb-32">
                    <DefaultInput showRequiredSign={true} label="Last Name" name="lastName" />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} className="section-block_content-item mb-32">
                    <DefaultInput showRequiredSign={true} label="Work email" name="workEmail" />
                  </Col>
                  {employee.availableStates && (
                    <Col xs={24} sm={24} md={8} lg={8} xl={8} className="section-block_content-item mb-32">
                      <DefaultSelect
                        showRequiredSign={true}
                        name="stateOfResidenceId"
                        placeholder="Select state of residence"
                        label="State of residence"
                        filterOption={filterByKeyAndValue}
                        showSearch
                      >
                        {employee.availableStates?.map((state) => (
                          <Option key={state.id} value={state.id}>
                            {state.id}
                          </Option>
                        ))}
                      </DefaultSelect>
                    </Col>
                  )}
                  <Col sm={24} md={4} className="section-block_content-item">
                    <InputMask
                      showRequiredSign={true}
                      name="zipCode"
                      label="Zip Code"
                      placeholder="00000"
                      mask="00000"
                    />
                  </Col>
                  <Col sm={24} md={12} className="section-block_content-item">
                    <DatePicker
                      popupClassName={'t-birthdate-popup'}
                      optional={!formik.values.requireBirthDate}
                      name="birthDay"
                      placeholder={'MM/DD/YYYY'}
                      id="birthDay"
                      className="w-100"
                      noStyles
                      defaultValue={formik.values.birthDay}
                      hideError={true}
                      showRequiredSign={formik.values.requireBirthDate}
                      label="Birth date"
                      disabledDate={disabledBirthDate}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} className="section-block_content-item mb-24">
                    <DefaultInput optional={true} name="ein" label="Employee Identification Number" />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} className="section-block_content-item mb-24">
                    <DefaultInput optional={true} label="Mobile phone number" name="workPhone" />
                  </Col>
                </Row>
              </SectionBlock>

              <SectionBlock
                mobileOnly={true}
                titleElement={
                  <>
                    <Icon className="far fa-suitcase mr-8" />
                    <Category title={translate('admin-dashboard.manage-people.complete-profile.employment-details')} />
                  </>
                }
              >
                <Row>
                  <Col span={24} className="mb-8">
                    <Label>
                      <RedAsterisk /> {translate('admin-dashboard.manage-people.edit-profile.hire-date-label')}
                    </Label>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} className="section-block_content-item mb-32">
                    <Radio.Group
                      name="hireDateSelect"
                      value={formik.values.hireDateSelect}
                      // @ts-ignore
                      error={formik.errors.hireDate}
                      onChange={(e) =>
                        handleEligibilityDateChange(
                          formik.values.productClassId,
                          e.target.value,
                          formik.values.hireDate?.toString(),
                          formik.values.benefitStartDate?.toString(),
                          formik,
                        )
                      }
                    >
                      <Row>
                        <Col span={24}>
                          <Radio value={0} className="hire_date_radio">
                            <DatePicker
                              popupClassName={'t-hire-date-popup'}
                              name="hireDate"
                              id="hireDate"
                              noStyles
                              disabled={formik.values.hireDateSelect !== 0}
                              defaultValue={initialValues.hireDate}
                              hideError={true}
                              onChange={(e) => {
                                formik.values.productClassId &&
                                  handleEligibilityDateChange(
                                    formik.values.productClassId,
                                    formik.values.hireDateSelect,
                                    e?.toString(),
                                    formik.values.benefitStartDate?.toString(),
                                    formik,
                                  )
                              }}
                            />
                          </Radio>
                        </Col>

                        <Col span={24}>
                          <Radio value={1} className="hire_date_radio pl">
                            <div className={'flex'}>
                              <div>
                                <p className={'mb-0'}>
                                  {translate('admin-dashboard.manage-people.hire-date-msg')}
                                  <b>{parseZoneFormat(initialValues.benefitStartDate)}</b>
                                </p>
                              </div>
                            </div>
                          </Radio>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Col>
                  <Col span={24} className="mb-8">
                    <Label>
                      <RedAsterisk /> {translate('admin-dashboard.manage-people.edit-profile.employment-status-label')}
                    </Label>
                  </Col>
                  <Col span={24} className="section-block_content-item mb-24">
                    <Radio.Group
                      name="employmentStatus"
                      value={formik.values.employmentStatus}
                      onChange={(e) => {
                        const availableProductClasses = filterProductClassesByEmploymentStatus(
                          employee.productClasses,
                          e.target.value,
                          initialValues.currentCompanyTypeId,
                        )
                        setProductClasses(availableProductClasses)

                        if (
                          availableProductClasses.length == 0 ||
                          !isEmployeeEligible(e.target.value, initialValues.currentCompanyTypeId)
                        ) {
                          formik.setFieldValue('productClassId', null)
                        }

                        if (!initialValues.allowProductClassSelection && availableProductClasses.length > 0) {
                          formik.setFieldValue('productClassId', availableProductClasses[0].id)
                          handleEligibilityDateChange(
                            availableProductClasses[0].id,
                            formik.values.hireDateSelect,
                            formik.values.hireDate?.toString(),
                            formik.values.benefitStartDate?.toString(),
                            formik,
                          )
                        }

                        const availableRenewalClasses = filterProductClassesByEmploymentStatus(
                          initialValues.renewalProductClasses,
                          e.target.value,
                          initialValues.currentCompanyTypeId,
                        )
                        setRenewalProductClasses(availableRenewalClasses)

                        if (
                          availableRenewalClasses.length == 0
                          //||
                          // !isEmployeeEligible(e.target.value, initialValues.currentCompanyTypeId)
                        ) {
                          formik.setFieldValue('renewalProductClassId', null)
                        }

                        if (
                          !initialValues.allowProductClassSelection &&
                          availableRenewalClasses.length > 0 &&
                          renewalProductClasses.length > 0
                        ) {
                          formik.setFieldValue('renewalProductClassId', availableRenewalClasses[0].id)
                        }
                      }}
                    >
                      <Row gutter={16}>
                        {employee.employmentStatuses.map((item) => (
                          <Col xs={24} sm={24} md={12} lg={12} xl={12} className={'mb-8'}>
                            <Radio.Button
                              className="custom-small-without-title radio-with-checkmark w-100"
                              value={item.id}
                            >
                              {item.name}
                            </Radio.Button>
                          </Col>
                        ))}
                      </Row>
                    </Radio.Group>
                  </Col>
                  {employee.allowProductClassSelection &&
                    productClasses.length > 0 &&
                    formik.values.employmentStatus != null && (
                      <>
                        <Col span={24} className="mb-8">
                          <Label>
                            <RedAsterisk /> Benefit class
                          </Label>
                        </Col>

                        <Col span={24} className="section-block_content-item mt-8 mb-24">
                          <Radio.Group
                            name="productClassId"
                            onChange={(e) =>
                              handleEligibilityDateChange(
                                e.target.value,
                                formik.values.hireDateSelect,
                                formik.values.hireDate?.toString(),
                                formik.values.benefitStartDate?.toString(),
                                formik,
                              )
                            }
                          >
                            <Row gutter={10}>
                              {productClasses.map((item) => (
                                <Col xs={24} sm={24} md={12} lg={12} xl={12} className={'mb-8'}>
                                  <Radio.Button
                                    className="custom-small-without-title radio-with-checkmark w-100"
                                    value={item.id}
                                  >
                                    {item.name}
                                  </Radio.Button>
                                </Col>
                              ))}
                            </Row>
                          </Radio.Group>
                        </Col>
                      </>
                    )}
                  {showWaitingPeriodAlert(formik) && eligibilityDate && formik.values.productClassId && (
                    <Col span={24} className="section-block_content-item mb-24">
                      <Alert
                        type="info"
                        showIcon
                        message="Waiting Period Policy"
                        description={`This employee will be eligible for the benefit on ${parseZoneFormat(
                          eligibilityDate,
                        )}`}
                      />
                    </Col>
                  )}
                  {employee.allowProductClassSelection &&
                    renewalProductClasses.length > 0 &&
                    formik.values.employmentStatus != null && (
                      <>
                        <Col span={24} className="mb-8">
                          <Label>
                            <RedAsterisk /> Renewal Benefit class
                          </Label>
                        </Col>

                        <Col span={24} className="section-block_content-item mt-8 mb-24">
                          <Radio.Group name="renewalProductClassId">
                            <Row gutter={10}>
                              {renewalProductClasses.map((item) => (
                                <Col xs={24} sm={24} md={12} lg={12} xl={12} className={'mb-8'}>
                                  <Radio.Button
                                    className="custom-small-without-title radio-with-checkmark w-100"
                                    value={item.id}
                                  >
                                    {item.name}
                                  </Radio.Button>
                                </Col>
                              ))}
                            </Row>
                          </Radio.Group>
                        </Col>
                      </>
                    )}

                  {initialValues.showEmployeeNotificationDate &&
                    productClasses.length > 0 &&
                    formik.values.employmentStatus != null && (
                      <Col sm={24} md={12} className="section-block_content-item">
                        <DefaultDatePickerV2
                          popupClassName={'t-notification-popup'}
                          name="notificationDate"
                          placeholder={'MM/DD/YYYY'}
                          id="notificationDate"
                          showRequiredSign={true}
                          disabled={!userNotificationDateInfo.isEmployeeNotificationDateEditable}
                          disabledDate={(current) =>
                            current &&
                            (isBefore(current, userNotificationDateInfo.minNotificationDate) ||
                              !userNotificationDateInfo.maxNotificationDate ||
                              isAfter(current, userNotificationDateInfo.maxNotificationDate))
                          }
                          label="Notification Date"
                        />
                      </Col>
                    )}
                </Row>
              </SectionBlock>

              <div className={'footer-wrap'}>
                <div className="footer-buttons">
                  <div>
                    <Button
                      onClick={() => {
                        if (props.onCancel) {
                          props.onCancel()
                        }
                      }}
                      className=" mb-8"
                      style={{ width: '100%' }}
                    >
                      Cancel
                    </Button>
                  </div>

                  <div>
                    <Button
                      onClick={() => submitForm(formik, true)}
                      type="default"
                      disabled={
                        !formValidator(translate, initialValues, productClasses, renewalProductClasses).isValidSync(
                          formik.values,
                        ) || isCommandLoading
                      }
                      className="outlined_button mb-8"
                      loading={isCommandLoading}
                      style={{ width: '100%' }}
                    >
                      Save and add more
                    </Button>
                  </div>

                  <div>
                    <Button
                      onClick={() => submitForm(formik, false)}
                      type="primary"
                      className="mb-8"
                      disabled={
                        !formValidator(translate, initialValues, productClasses, renewalProductClasses).isValidSync(
                          formik.values,
                        ) || isCommandLoading
                      }
                      loading={isCommandLoading}
                      style={{ width: '100%' }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      )}
    </Skeleton>
  )
}

export default AddPeopleForm

const formValidator = (
  translate: any,
  initialValues: EmployeeForSaveDto,
  productClasses: ProductClassForEmployeeForSave[],
  renewalProductClasses: ProductClassForEmployeeForSave[],
) =>
  Yup.object({
    firstName: Yup.string().nullable().required(translate('general.required')),
    lastName: Yup.string().nullable().required(translate('general.required')),
    zipCode: Yup.string()
      .trim()
      .nullable()
      .matches(zipNumberRegex, translate('admin-signup.company-details.errors.zip-number-regex'))
      .required(translate('general.required')),

    workEmail: Yup.string()
      .nullable()
      .required(translate('general.required'))
      .email(translate('admin-signup.signup.errors.email')),

    workPhone: Yup.number()
      .typeError('Should be a number')
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .nullable(),
    birthDay: Yup.string()
      .nullable()
      .when('requireBirthDate', {
        is: (requireBirthDate: boolean) => requireBirthDate,
        then: Yup.string().required(translate('general.required')),
      }),
    stateOfResidenceId: Yup.string().nullable().required(translate('general.required')),
    employmentStatus: Yup.number().nullable().required(translate('general.required')),
    hireDateSelect: Yup.number().nullable().required(translate('general.required')),
    hireDate: Yup.string()
      .nullable()
      .when('hireDateSelect', {
        is: (hireDateSelect: number) => {
          return hireDateSelect === 0
        },
        then: Yup.string().required(translate('general.required')),
      }),

    notificationDate: Yup.string()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) =>
          productClasses.length > 0 && initialValues.showEmployeeNotificationDate,
        then: Yup.string().required(translate('general.required')),
      }),
    productClassId: Yup.string()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) => {
          return productClasses.length > 0
        },
        then: Yup.string().required(translate('general.required')),
      }),

    renewalProductClassId: Yup.string()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) => {
          return renewalProductClasses.length > 0
        },
        then: Yup.string().required(translate('general.required')),
      }),
  })

type FormData = EmployeeForSaveDto & {
  hireDateSelect?: HireDateRadioSelect
}

enum HireDateRadioSelect {
  Date = 0,
  IDontKnow = 1,
}
